import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Camera, Upload, CheckCircle } from 'lucide-react';
import { useApp } from '../context/useApp.js';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import { userService } from '../services/firebaseService.js';
import toast from 'react-hot-toast';

const UserProfile = () => {
  const navigate = useNavigate();
  const { language, setProfileComplete, addActivity } = useApp();
  const { user: currentUser } = useFirebaseAuth();
  
  // Check if this is a new user flow (coming from registration)
  const [isNewUserFlow, setIsNewUserFlow] = useState(false);
  
  useEffect(() => {
    // Check if user just registered and needs to create profile
    const justRegistered = localStorage.getItem('justRegistered');
    if (justRegistered === 'true') {
      setIsNewUserFlow(true);
      localStorage.removeItem('justRegistered');
    }
  }, []);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    dateOfBirth: '',
    city: '',
    customCity: '',
    bio: '',
    mobileNumber: '',
    aadharNumber: '',
    profilePicture: null
  });
  const [isHuman, setIsHuman] = useState(false);
  const [isProfilePicVerified, setIsProfilePicVerified] = useState(false);
  const [errors, setErrors] = useState({});
  const [showCustomCity, setShowCustomCity] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const fileInputRef = useRef(null);

  const translations = {
    en: {
      title: 'Create Your Profile',
      subtitle: 'Complete your profile to start finding legal help',
      fullName: 'Full Name',
      email: 'Email Address',
      dateOfBirth: 'Date of Birth',
      city: 'City',
      customCity: 'Custom City',
      bio: 'Bio',
      mobileNumber: 'Mobile Number',
      aadharNumber: 'Aadhar Card Number',
      profilePicture: 'Profile Picture',
      uploadPhoto: 'Upload Photo',
      liveVerification: 'Live Human Verification',
      startVerification: 'Start Verification',
      verificationSuccess: 'Verification successful!',
      saveProfile: 'Save Profile',
      required: 'This field is required',
      profileSaved: 'Profile saved successfully!',
      selectCity: 'Select your city',
      enterCustomCity: 'Enter custom city name',
      addCustomCity: 'Add Custom City',
      backToCityList: 'Back to City List',
      cityHelper: 'Select your city from the list or add a custom city if yours is not listed',
      tellAboutYourself: 'Tell us about yourself (optional)',
      maxCharacters: 'Maximum 200 characters'
    },
    hi: {
      title: 'अपना प्रोफाइल बनाएं',
      subtitle: 'कानूनी सहायता प्राप्त करने के लिए अपना प्रोफाइल पूरा करें',
      fullName: 'पूरा नाम',
      email: 'ईमेल पता',
      dateOfBirth: 'जन्म तिथि',
      city: 'शहर',
      customCity: 'कस्टम शहर',
      bio: 'जीवनी',
      mobileNumber: 'मोबाइल नंबर',
      aadharNumber: 'आधार कार्ड नंबर',
      profilePicture: 'प्रोफाइल फोटो',
      uploadPhoto: 'फोटो अपलोड करें',
      liveVerification: 'लाइव मानव सत्यापन',
      startVerification: 'सत्यापन शुरू करें',
      verificationSuccess: 'सत्यापन सफल!',
      saveProfile: 'प्रोफाइल सहेजें',
      required: 'यह फील्ड आवश्यक है',
      profileSaved: 'प्रोफाइल सफलतापूर्वक सहेजा गया!',
      selectCity: 'अपना शहर चुनें',
      enterCustomCity: 'कस्टम शहर का नाम दर्ज करें',
      addCustomCity: 'कस्टम शहर जोड़ें',
      backToCityList: 'शहर सूची में वापस जाएं',
      cityHelper: 'सूची से अपना शहर चुनें यदि आपका शहर सूची में नहीं है तो कस्टम शहर जोड़ें',
      tellAboutYourself: 'हमें अपने बारे में बताएं (वैकल्पिक)',
      maxCharacters: 'अधिकतम 200 अक्षर'
    }
  };

  const t = translations[language] || translations.en;

  const cities = [
    'Mumbai, Maharashtra',
    'Delhi, Delhi',
    'Bangalore, Karnataka',
    'Hyderabad, Telangana',
    'Chennai, Tamil Nadu',
    'Kolkata, West Bengal',
    'Pune, Maharashtra',
    'Ahmedabad, Gujarat',
    'Jaipur, Rajasthan',
    'Surat, Gujarat',
    'Lucknow, Uttar Pradesh',
    'Kanpur, Uttar Pradesh',
    'Nagpur, Maharashtra',
    'Indore, Madhya Pradesh',
    'Thane, Maharashtra',
    'Bhopal, Madhya Pradesh',
    'Visakhapatnam, Andhra Pradesh',
    'Pimpri-Chinchwad, Maharashtra',
    'Patna, Bihar',
    'Vadodara, Gujarat'
  ];

  // Load existing profile data on component mount
  useEffect(() => {
    const loadProfile = async () => {
      if (currentUser && (currentUser?.id || currentUser?.uid)) {
        try {
          console.log('Loading profile for user:', currentUser.id || currentUser.uid);
          const result = await userService.getUserProfile(currentUser.id || currentUser.uid);
          
          if (result.success && result.data) {
            const profile = result.data;
            console.log('Loaded existing profile:', profile);
            setFormData({
              fullName: profile.full_name || '',
              email: profile.email || currentUser.email || '',
              dateOfBirth: profile.date_of_birth || '',
              city: profile.city || '',
              customCity: profile.custom_city || '',
              bio: profile.bio || '',
              mobileNumber: profile.phone || '',
              aadharNumber: profile.aadhar_number || '',
              profilePicture: profile.profile_picture || null
            });
          } else {
            // Set default email from current user for new profiles
            console.log('No existing profile found, setting default email');
            setFormData(prev => ({
              ...prev,
              email: currentUser.email || ''
            }));
          }
        } catch (error) {
          console.error('Error loading profile:', error);
          // Set default email from current user
          setFormData(prev => ({
            ...prev,
            email: currentUser.email || ''
          }));
        }
      }
      setIsLoading(false);
    };

    loadProfile();
  }, [currentUser]);

  // Redirect if user is not authenticated or is not a client
  useEffect(() => {
    // Check if this is initial profile creation (no authentication required)
    const isInitialProfileCreation = window.location.pathname === '/create-profile';
    const selectedRole = localStorage.getItem('selectedRole');
    
    // Allow access if user is creating profile for the first time
    if (!currentUser && !isInitialProfileCreation) {
      navigate('/role-selection');
      return;
    }
    
    // If user has a role and it's not client, redirect appropriately
    if (currentUser && selectedRole && selectedRole !== 'client') {
      if (selectedRole === 'lawyer') {
        navigate('/lawyer-profile');
      } else {
        navigate('/role-selection');
      }
      return;
    }
    
    // If profile is already complete and user is authenticated, redirect to dashboard
    if (currentUser && currentUser.isProfileComplete) {
      navigate('/dashboard');
      return;
    }
  }, [currentUser, navigate]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = t.required;
    }

    if (!formData.email.trim()) {
      newErrors.email = t.required;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = t.required;
    }

    if (!formData.city && !formData.customCity) {
      newErrors.city = t.required;
    }

    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = t.required;
    } else if (!/^\d{10}$/.test(formData.mobileNumber.replace(/\D/g, ''))) {
      newErrors.mobileNumber = 'Please enter a valid 10-digit mobile number';
    }

    if (!formData.aadharNumber.trim()) {
      newErrors.aadharNumber = t.required;
    } else if (!/^\d{12}$/.test(formData.aadharNumber.replace(/\D/g, ''))) {
      newErrors.aadharNumber = 'Please enter a valid 12-digit Aadhar number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error('File size should be less than 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData(prev => ({
          ...prev,
          profilePicture: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveProfile = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors before saving');
      return;
    }

    // Check if this is initial profile creation (no authentication required)
    const isInitialProfileCreation = window.location.pathname === '/create-profile';
    const selectedRole = localStorage.getItem('selectedRole');
    
    if (!currentUser && !isInitialProfileCreation) {
      toast.error('User not authenticated');
      return;
    }

    setIsSaving(true);

    try {
             if (isInitialProfileCreation && !currentUser) {
        // For initial profile creation without authentication
        const profileData = {
          full_name: formData.fullName.trim(),
          email: formData.email.trim(),
          date_of_birth: formData.dateOfBirth,
          city: formData.city || formData.customCity,
          custom_city: formData.customCity || null,
          bio: formData.bio.trim(),
          phone: formData.mobileNumber.trim(),
          aadhar_number: formData.aadharNumber.trim(),
          profile_picture: formData.profilePicture,
          profile_complete: true,
          role: 'client'
        };

        // Store profile data locally for later use
        localStorage.setItem('pendingProfileData', JSON.stringify(profileData));
        localStorage.setItem('isProfileComplete', 'true');
        
        toast.success('Profile created successfully! Please sign in with your email to continue.', {
          duration: 4000,
          position: 'top-center'
        });
        
        setTimeout(() => {
          navigate('/auth');
        }, 2000);
      } else {
        // For authenticated users
                 console.log('Starting profile save for user:', currentUser.id || currentUser.uid);
        
        const profileData = {
          full_name: formData.fullName.trim(),
          email: formData.email.trim(),
          date_of_birth: formData.dateOfBirth,
          city: formData.city || formData.customCity,
          custom_city: formData.customCity || null,
          bio: formData.bio.trim(),
          phone: formData.mobileNumber.trim(),
          aadhar_number: formData.aadharNumber.trim(),
          profile_picture: formData.profilePicture,
          profile_complete: true,
          role: 'client'
        };

        console.log('Profile data to save:', profileData);

                 const result = await userService.upsertUserProfile(currentUser.id || currentUser.uid, profileData);
        console.log('Profile save result:', result);

        if (result.success) {
          console.log('Profile saved successfully');
          
          // Update profile completion status
          setProfileComplete(true);
          
          // Set localStorage flag for profile completion
          localStorage.setItem('userProfileComplete', 'true');
          
          // Add activity
          addActivity({
            type: 'profile_created',
            description: 'Profile created successfully',
            timestamp: new Date().toISOString()
          });

          toast.success(t.profileSaved, {
            duration: 3000,
            position: 'top-center'
          });

          // For authenticated users, always go to dashboard after profile completion
          setTimeout(() => {
            navigate('/dashboard');
          }, 1500);
        } else {
          console.error('Profile save failed:', result.error);
          toast.error(result.error || 'Failed to save profile');
        }
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Failed to save profile: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">{t.title}</h1>
                <p className="text-sm text-gray-600">{t.subtitle}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Profile Form */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-6">
              {/* Profile Picture */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.profilePicture}
                </label>
                <div className="flex items-center space-x-4">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center overflow-hidden">
                    {formData.profilePicture ? (
                      <img
                        src={formData.profilePicture}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Camera className="w-8 h-8 text-gray-400" />
                    )}
                  </div>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    {t.uploadPhoto}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </div>
              </div>

              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.fullName} *
                </label>
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => handleInputChange('fullName', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                    errors.fullName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your full name"
                />
                {errors.fullName && (
                  <p className="mt-1 text-sm text-red-600">{errors.fullName}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.email} *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your email"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>

              {/* Date of Birth */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.dateOfBirth} *
                </label>
                <input
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                    errors.dateOfBirth ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.dateOfBirth && (
                  <p className="mt-1 text-sm text-red-600">{errors.dateOfBirth}</p>
                )}
              </div>

              {/* City */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.city} *
                </label>
                {!showCustomCity ? (
                  <div className="space-y-2">
                    <select
                      value={formData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                        errors.city ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">{t.selectCity}</option>
                      {cities.map((city) => (
                        <option key={city} value={city}>
                          {city}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setShowCustomCity(true)}
                      className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                    >
                      {t.addCustomCity}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={formData.customCity}
                      onChange={(e) => handleInputChange('customCity', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                        errors.city ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder={t.enterCustomCity}
                    />
                    <button
                      type="button"
                      onClick={() => setShowCustomCity(false)}
                      className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                    >
                      {t.backToCityList}
                    </button>
                  </div>
                )}
                {errors.city && (
                  <p className="mt-1 text-sm text-red-600">{errors.city}</p>
                )}
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Mobile Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.mobileNumber} *
                </label>
                <input
                  type="tel"
                  value={formData.mobileNumber}
                  onChange={(e) => handleInputChange('mobileNumber', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                    errors.mobileNumber ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your mobile number"
                />
                {errors.mobileNumber && (
                  <p className="mt-1 text-sm text-red-600">{errors.mobileNumber}</p>
                )}
              </div>

              {/* Aadhar Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.aadharNumber} *
                </label>
                <input
                  type="text"
                  value={formData.aadharNumber}
                  onChange={(e) => handleInputChange('aadharNumber', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                    errors.aadharNumber ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your Aadhar number"
                  maxLength="12"
                />
                {errors.aadharNumber && (
                  <p className="mt-1 text-sm text-red-600">{errors.aadharNumber}</p>
                )}
              </div>

              {/* Bio */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.bio}
                </label>
                <textarea
                  value={formData.bio}
                  onChange={(e) => handleInputChange('bio', e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder={t.tellAboutYourself}
                  maxLength={200}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {formData.bio.length}/200 {t.maxCharacters}
                </p>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={handleSaveProfile}
              disabled={isSaving}
              className="w-full bg-purple-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : t.saveProfile}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;